import { TServerErrorResponse } from "@/types/errors";
import { http } from "../httpHandler";
import { EntityType, PermissionLevel } from "@/types/entity";

export const AUTOSHARE_LIST = "autoshare-list";
export const AUTOSHARE_CREATE = "autoshare-create";
export const AUTOSHARE_DELETE = "autoshare-delete";

/**
 * AutoShare configuration interface
 * @property {string} id - Unique identifier for the autoshare configuration
 * @property {string} firmId - The firm ID
 * @property {string} userId - The user who created the autoshare configuration
 * @property {EntityType} entityType - Type of entity being shared (CONVERSATION or CLIENT)
 * @property {string} targetUserId - The user who will receive access to the entity (optional)
 * @property {string} targetGroupId - The group that will receive access to the entity (optional)
 * @property {PermissionLevel} level - Permission level (READ or WRITE)
 * @property {boolean} active - Whether the configuration is active
 * @property {number} createdAt - Timestamp of creation
 * @property {number} updatedAt - Timestamp of last update
 * @property {string} updatedBy - User who last updated the configuration
 */
export interface AutoShareConfig {
  id: string;
  firmId: string;
  userId: string;
  entityType: EntityType;
  targetUserId?: string;
  targetGroupId?: string;
  level: PermissionLevel;
  configType: "USER" | "FIRM";
  active: boolean;
  createdAt: number;
  updatedAt: number;
  updatedBy: string;
}

/**
 * Payload for creating a new AutoShare configuration
 * @property {EntityType} entityType - Type of entity to share (CONVERSATION or CLIENT)
 * @property {string} targetUserId - ID of the user to share with (required if targetGroupId not provided)
 * @property {string} targetGroupId - ID of the group to share with (required if targetUserId not provided)
 * @property {PermissionLevel} level - Permission level (READ or WRITE)
 */
export interface AutoShareCreatePayload {
  entityType: EntityType;
  targetUserId?: string;
  targetGroupId?: string;
  level: PermissionLevel;
  configType: "USER" | "FIRM";
  active: boolean;
}

/**
 * Fetches all autoshare configurations for the current user
 * @returns {Promise<AutoShareConfig[]>} A promise that resolves to an array of AutoShare configurations
 */
export const findAllForUser = async (): Promise<AutoShareConfig[]> => {
  try {
    const response = await http.get<AutoShareConfig[]>(`/autoshare`);
    return response.data;
  } catch (error) {
    throw error as TServerErrorResponse;
  }
};

/**
 * Creates a new autoshare configuration
 * @param {AutoShareCreatePayload} data - The data for the new AutoShare configuration
 * @returns {Promise<AutoShareConfig>} A promise that resolves to the created AutoShare configuration
 */
export const create = async (
  data: AutoShareCreatePayload
): Promise<AutoShareConfig> => {
  try {
    const response = await http.post<AutoShareConfig>(`/autoshare`, data);
    return response.data;
  } catch (error) {
    throw error as TServerErrorResponse;
  }
};

/**
 * Deletes an autoshare configuration
 * @param {string} id - The ID of the AutoShare configuration to delete
 * @returns {Promise<void>} A promise that resolves when the configuration is deleted
 */
export const remove = async (id: string): Promise<void> => {
  try {
    await http.delete(`/autoshare/${id}`);
  } catch (error) {
    throw error as TServerErrorResponse;
  }
}; 